import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Heart,
  DollarSign,
  Users,
  Calendar,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Home,
  TrendingUp,
  Target,
  Clock,
  Loader2,
  MessageCircle,
  Mail,
  Phone
} from 'lucide-react';

const DonationPublicPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [appel, setAppel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const [donData, setDonData] = useState({
    montant: '',
    nom_donateur: '',
    prenom_donateur: '',
    email_donateur: '',
    telephone_donateur: '',
    numero_whatsapp: '',
    objet_don: '',
    projet_soutenu: '',
    methode_paiement: 'mobile_money',
    anonyme: false,
    message_donateur: ''
  });
   let Url_base= process.env.REACT_APP_API_URL;
  // Charger les données de l'appel via le token
  const loadAppel = async () => {
    try {
      setLoading(true);
      
      // Essayer d'abord avec le token original
      let response = await fetch(Url_base+`/don/${token}`, {
        headers: {
          'Accept': 'application/json',
        }
      });

      console.log(`Chargement de l'appel avec le token: ${token}`, response);

      // Si 404, essayer avec des corrections automatiques de token
      if (response.status === 404) {
        // console.log('Token original échoué, tentative de correction automatique...');
        
        // Corrections courantes : remplacer les caractères similaires
        const tokenCorrections = [
          token.replace(/z+/g, 's'), // Remplacer z par s
          token.replace(/Z+/g, 's'), // Remplacer Z par s
          token.replace(/l/g, 'I'),  // Remplacer l par I
          token.replace(/I/g, 'l'),  // Remplacer I par l
          token.replace(/0/g, 'O'),  // Remplacer 0 par O
          token.replace(/O/g, '0'),  // Remplacer O par 0
        ];

        for (const correctedToken of tokenCorrections) {
          if (correctedToken !== token) {
            console.log(`Essai avec token corrigé: ${correctedToken}`);
            response = await fetch(Url_base+`/don/${correctedToken}`, {
              headers: {
                'Accept': 'application/json',
              }
            });
            
            if (response.ok) {
              console.log(`Token corrigé trouvé: ${correctedToken}`);
              // Rediriger vers l'URL correcte
              window.history.replaceState({}, '', `/don/${correctedToken}`);
              break;
            }
          }
        }
      }

      const data = await response.json();
      console.log('Données reçues:', data);

      if (response.ok) {
        // Attacher les dons récents à l'appel pour faciliter l'accès
        const appelWithDons = {
          ...data.data.appel,
          dons_recents: data.data.dons_recents || []
        };
        setAppel(appelWithDons);
        setError(null); // Clear any previous errors
      } else {
        if (response.status === 404) {
          setError('Lien de donation invalide ou expiré. Vérifiez l\'URL ou contactez l\'organisateur.');
        } else {
          setError(data.message || 'Appel à contribution introuvable');
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setError('Erreur lors du chargement de la page. Vérifiez votre connexion.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      loadAppel();
    }
  }, [token]);

  // Soumettre le don
  const handleSubmitDon = async (e) => {
    e.preventDefault();
    
    if (!donData.montant || donData.montant < 100) {
      setError('Le montant minimum est de 100 XOF');
      return;
    }

    if (!donData.anonyme) {
      if (!donData.nom_donateur.trim() || !donData.prenom_donateur.trim()) {
        setError('Veuillez renseigner votre nom et votre prénom ou cocher "Don anonyme"');
        return;
      }

      if (!donData.email_donateur.trim()) {
        setError('Veuillez renseigner votre email');
        return;
      }

      if (!donData.numero_whatsapp.trim() && !donData.telephone_donateur.trim()) {
        setError('Veuillez renseigner votre numéro WhatsApp ou téléphone');
        return;
      }
    }

    try {
      setSubmitting(true);
      setError(null);
      
      const response = await fetch(Url_base+'/dons', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          ...donData,
          nom_donateur: donData.nom_donateur.trim(),
          prenom_donateur: donData.prenom_donateur.trim(),
          email_donateur: donData.email_donateur.trim(),
          telephone_donateur: donData.telephone_donateur.trim() || donData.numero_whatsapp.trim(),
          numero_whatsapp: donData.numero_whatsapp.trim() || donData.telephone_donateur.trim(),
          objet_don: donData.objet_don.trim(),
          projet_soutenu: donData.projet_soutenu.trim(),
          appel_don_id: appel.id
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        // Recharger les données de l'appel pour mettre à jour les statistiques
        const updatedResponse = await fetch(Url_base+`/don/${token}`, {
          headers: {
            'Accept': 'application/json',
          }
        });
        
        if (updatedResponse.ok) {
          const updatedData = await updatedResponse.json();
          console.log('Données mises à jour après don:', updatedData);
          // Attacher les dons récents à l'appel
          const appelWithDons = {
            ...updatedData.data.appel,
            dons_recents: updatedData.data.dons_recents || []
          };
          setAppel(appelWithDons);
        }
      } else {
        setError(data.message || 'Erreur lors de l\'enregistrement du don');
      }
    } catch (error) {
      console.error('Erreur:', error);
      setError('Erreur lors de l\'enregistrement du don');
    } finally {
      setSubmitting(false);
    }
  };

  // Helper pour formater les montants
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF'
    }).format(amount || 0);
  };

  // Helper pour calculer le pourcentage de progression
  const getPourcentage = () => {
    if (!appel || !appel.objectif_montant || appel.objectif_montant === 0) return 0;
    return Math.min((parseFloat(appel.montant_collecte || 0) / parseFloat(appel.objectif_montant)) * 100, 100);
  };

  // Helper pour vérifier si l'appel est expiré
  const isExpired = () => {
    if (!appel?.date_fin) return false;
    return new Date(appel.date_fin) < new Date();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Erreur</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          
          {error.includes('invalide') && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-yellow-800 mb-3">💡 <strong>Solutions :</strong></p>
              <ul className="text-sm text-yellow-700 space-y-1 mb-4">
                <li>• Vérifiez que l'URL est complète</li>
                <li>• Assurez-vous de ne pas avoir modifié le lien</li>
                <li>• Essayez le lien de test ci-dessous</li>
              </ul>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-800 mb-2">🔗 <strong>Lien de test valide :</strong></p>
                <button
                  onClick={() => window.location.href = '/don/yCnzX0rJ2FnITYRszZxhkDPgjbsute0t'}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                >
                  Accéder à la page de donation
                </button>
              </div>
            </div>
          )}
          
          <div className="space-y-3">
            <button
              onClick={() => {
                setError(null);
                loadAppel();
              }}
              className="w-full px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Réessayer
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full px-6 py-3 border-2 border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center"
            >
              <Home className="w-4 h-4 mr-2" />
              Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Don Enregistré !</h1>
          <p className="text-gray-600 mb-6">
            Merci pour votre généreuse contribution de <span className="font-bold text-green-600">{formatCurrency(donData.montant)}</span> ! 
            Votre don a été enregistré avec succès.
          </p>
          
          {/* Affichage des nouvelles statistiques */}
          {appel && (
            <div className="bg-green-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-green-700 mb-2">Nouveau total collecté :</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(parseFloat(appel.montant_collecte || 0))}</p>
              <p className="text-sm text-green-600">par {appel.total_dons || 0} donateur{(appel.total_dons || 0) > 1 ? 's' : ''}</p>
            </div>
          )}
          
          <div className="space-y-3">
            <button
              onClick={() => {
                setSuccess(false);
                setDonData({
                  montant: '',
                  nom_donateur: '',
                  prenom_donateur: '',
                  email_donateur: '',
                  telephone_donateur: '',
                  numero_whatsapp: '',
                  objet_don: '',
                  projet_soutenu: '',
                  methode_paiement: 'mobile_money',
                  anonyme: false,
                  message_donateur: ''
                });
              }}
              className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Faire un autre don
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full px-6 py-3 border-2 border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center"
            >
              <Home className="w-4 h-4 mr-2" />
              Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-amber-50">
      {/* Header */}
      <header className="bg-white shadow-lg border-b-4 border-gradient-to-r from-blue-500 to-amber-500">
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Heart className="w-8 h-8 sm:w-10 sm:h-10 text-red-500" />
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-gray-900">
                  <span className="hidden sm:inline">Fleurs de la Sainte Famille</span>
                  <span className="sm:hidden">FSF</span>
                </h1>
                <p className="text-xs sm:text-sm text-gray-600">Don en ligne</p>
              </div>
            </div>
            
            <button
              onClick={() => navigate('/')}
              className="flex items-center px-3 sm:px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Retour</span>
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="max-w-4xl mx-auto">
          {/* Appel à contribution */}
          <div className="bg-white rounded-2xl shadow-xl mb-6 sm:mb-8 overflow-hidden">
            {/* Header de l'appel */}
            <div className="bg-gradient-to-r from-blue-600 to-amber-600 text-white p-6 sm:p-8">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3">
                    {appel.titre}
                  </h1>
                  <p className="text-blue-100 text-base sm:text-lg leading-relaxed">
                    {appel.description}
                  </p>
                </div>
                
                <div className="ml-4 flex-shrink-0">
                  <div className={`w-4 h-4 rounded-full ${
                    appel.type_don === 'urgence' ? 'bg-red-500' :
                    appel.type_don === 'projet' ? 'bg-green-500' :
                    appel.type_don === 'evenement' ? 'bg-amber-500' : 'bg-blue-500'
                  }`}></div>
                </div>
              </div>

              {/* Date de fin si définie */}
              {appel.date_fin && (
                <div className="flex items-center space-x-2 text-blue-100">
                  {isExpired() ? (
                    <Clock className="w-4 h-4 text-red-300" />
                  ) : (
                    <Calendar className="w-4 h-4" />
                  )}
                  <span className="text-sm">
                    {isExpired() ? 'Expiré le' : 'Jusqu\'au'} {new Date(appel.date_fin).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </span>
                </div>
              )}
            </div>

            <div className="p-6 sm:p-8">
              <form onSubmit={handleSubmitDon} autoComplete="off" className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Montant du don (XOF) *
                    </label>
                    <input
                      type="number"
                      min="100"
                      name="don_montant"
                      autoComplete="off"
                      value={donData.montant}
                      onChange={(e) => setDonData({ ...donData, montant: Number(e.target.value) })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                      placeholder="1000"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Méthode de paiement
                    </label>
                    <select
                      value={donData.methode_paiement}
                      onChange={(e) => setDonData({ ...donData, methode_paiement: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                    >
                      <option value="mobile_money">Mobile Money</option>
                      <option value="carte">Carte bancaire</option>
                      <option value="especes">Espèces</option>
                      <option value="virement">Virement bancaire</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center gap-3 rounded-lg bg-gray-50 p-3">
                  <input
                    type="checkbox"
                    checked={donData.anonyme}
                    onChange={(e) => setDonData({ ...donData, anonyme: e.target.checked })}
                    className="h-4 w-4 text-blue-600 rounded"
                  />
                  <label className="text-sm text-gray-700 font-medium">Don anonyme</label>
                </div>

                {!donData.anonyme && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Nom *</label>
                        <input
                          type="text"
                          name="nom_donateur"
                          autoComplete="family-name"
                          value={donData.nom_donateur}
                          onChange={(e) => setDonData({ ...donData, nom_donateur: e.target.value })}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                          placeholder="Dupont"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Prénom *</label>
                        <input
                          type="text"
                          name="prenom_donateur"
                          autoComplete="given-name"
                          value={donData.prenom_donateur}
                          onChange={(e) => setDonData({ ...donData, prenom_donateur: e.target.value })}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                          placeholder="Jean"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                        <input
                          type="email"
                          name="email_donateur"
                          autoComplete="email"
                          value={donData.email_donateur}
                          onChange={(e) => setDonData({ ...donData, email_donateur: e.target.value })}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                          placeholder="jean.dupont@email.com"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Numéro WhatsApp *</label>
                        <input
                          type="tel"
                          name="numero_whatsapp"
                          autoComplete="tel"
                          value={donData.numero_whatsapp}
                          onChange={(e) => setDonData({ ...donData, numero_whatsapp: e.target.value })}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                          placeholder="+229 97 00 00 00"
                          required
                        />
                      </div>
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Objet du don
                  </label>
                  <input
                    type="text"
                    name="objet_don"
                    autoComplete="off"
                    value={donData.objet_don}
                    onChange={(e) => setDonData({ ...donData, objet_don: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                    placeholder="Ex: Soutien au centre communautaire"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Projet à soutenir
                  </label>
                  <input
                    type="text"
                    name="projet_soutenu"
                    autoComplete="off"
                    value={donData.projet_soutenu}
                    onChange={(e) => setDonData({ ...donData, projet_soutenu: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                    placeholder="Ex: Construction du centre communautaire"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Message (optionnel)
                  </label>
                  <textarea
                    rows={4}
                    value={donData.message_donateur}
                    onChange={(e) => setDonData({ ...donData, message_donateur: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all resize-none"
                    placeholder="Votre message ou encouragement..."
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Enregistrement en cours...
                    </>
                  ) : (
                    'Valider mon don'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonationPublicPage;